import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fmtDate } from '@dcc-erp/shared';
import type { OpContractWithAccruals } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApproveOpContractStep,
  useRejectOpContractStep,
  useReturnOpContract,
  useSubmitOpContract,
} from '../../lib/queries/op-contracts';
import { AppModal } from '../AppModal';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  /** The fetched contract detail (carries the materialized approvals chain). */
  contract: OpContractWithAccruals;
  onClose: () => void;
}

// What's awaiting approval — drives the header copy.
function pendingActionLabel(pendingAction: string | null | undefined): string {
  if (pendingAction === 'TERMINATE') return 'รออนุมัติยกเลิก';
  if (pendingAction === 'REGISTER') return 'รออนุมัติทะเบียน';
  return 'รออนุมัติ';
}

// OpContract maker-checker panel (M3, docType OSC). Composes the shared
// ApprovalTimeline + DecisionModal, wired to the four engine hooks. Mirrors
// ApBillApprovalPanel: gates the decision/submit buttons with <Can>, surfaces
// the RETURNED reason, and shows the maker→reviewer→approver chain. Unlike
// AP-bill there are two gated actions (REGISTER / TERMINATE) discriminated by
// pendingAction — surfaced in the header copy.
export function OpContractApprovalPanel({ companyId, contract, onClose }: Props) {
  const submitMut = useSubmitOpContract(companyId);
  const approveMut = useApproveOpContractStep(companyId);
  const rejectMut = useRejectOpContractStep(companyId);
  const returnMut = useReturnOpContract(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  const approvalStatus = contract.approvalStatus ?? null;
  const currentStep = contract.currentStep ?? 0;
  const totalSteps = contract.approvals?.length ?? 0;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(contract.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          id: contract.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          id: contract.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          id: contract.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['op-contracts', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <AppModal
      title={`สายอนุมัติสัญญาดำเนินงาน — ${contract.code}`}
      subtitle={contract.vendorName ?? ''}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="apbill.create">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="apbill.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                ดำเนินการอนุมัติ
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* RETURNED reason banner (resubmit context) */}
      {approvalStatus === 'RETURNED' && contract.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="osc-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {contract.returnedReason}
        </div>
      )}

      {/* awaiting-step hint — carries the REGISTER/TERMINATE discriminator */}
      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> {pendingActionLabel(contract.pendingAction)} · ขั้น{' '}
          {currentStep}/{totalSteps}
        </div>
      )}

      <ApprovalTimeline
        approvals={contract.approvals ?? []}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          contract.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {contract.submittedBy}
              {contract.submittedAt ? ` · ${fmtDate(contract.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          approvalStatus === 'APPROVED' && contract.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {contract.approvedBy}
              {contract.approvedAt ? ` · ${fmtDate(contract.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="osc-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={contract.code}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาสัญญาดำเนินงาน"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
